import { Navigate } from "react-router-dom";
import { Link } from "@frontend/lib/link";
import { CollabRail } from "@frontend/components/collab/collab-rail";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { EmptyState } from "@frontend/components/ui";
import { branding } from "@creators/backend/lib/config";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";
import { formatINR } from "@creators/backend/lib/money";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { collabMatchesStatus } from "@frontend/lib/collab-status";
import { collabMatchesTab, filterActiveCollabs } from "@frontend/lib/collab-tabs";
import { useViewer } from "@frontend/lib/viewer";
import { useAsync } from "@frontend/lib/use-async";
import {
  sideToRole,
  useWorkspaceSide,
  WORKSPACE_SIDE_LABELS,
} from "@frontend/lib/workspace-side";
import { useEffect, useMemo } from "react";

/**
 * The inbox half of the collab room. A collab is a conversation, so its list lives
 * in the same window as the thread: picking one on the left fills the pane on
 * the right instead of navigating away to a different kind of page.
 */
export function CollabsPage() {
  const { viewer, loading: viewerLoading } = useViewer();
  const { side, canToggle } = useWorkspaceSide();
  useEffect(() => { document.title = `Collabs · ${branding.name}`; }, []);
  const state = useAsync(
    async () => {
      if (!viewer || viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs");
      return collabs;
    },
    [viewer?.sessionId, viewer?.isAdmin, viewer?.creator?.id],
    { cacheKey: collabsCacheKey(viewer?.sessionId, viewer?.creator?.id) },
  );

  const collabs = state.data ?? [];
  const roleSide = canToggle ? sideToRole(side) : null;
  const scoped = useMemo(
    () => (roleSide ? collabs.filter((collab) => collab.yourRole === roleSide) : collabs),
    [collabs, roleSide],
  );
  // Same “Active” set as the rail — quiet Basic offers sit under Other, not here.
  const live = useMemo(
    () =>
      filterActiveCollabs(scoped).filter((collab) => collabMatchesStatus(collab, "active")),
    [scoped],
  );

  if (viewerLoading) {
    return <PageSkeleton variant="inbox" />;
  }
  // Inbox is signed-in only — don't leave a blank shell for anonymous visits.
  if (!viewer) {
    return <Navigate to="/" replace />;
  }

  if (state.status === "loading" && !state.data) {
    return <PageSkeleton variant="inbox" />;
  }
  if (viewer.isAdmin || state.data === null) return null;

  if (collabs.length === 0) {
    return (
      <div className="mx-auto w-full max-w-6xl flex-1 overflow-y-auto px-4 py-10">
        <h1 className="mb-5 text-2xl font-semibold tracking-tight">Collabs</h1>
        <EmptyState title="No collabs yet">
          <NoCollabsBody viewer={viewer} />
        </EmptyState>
      </div>
    );
  }

  const waiting = live.filter((collab) => collabMatchesTab(collab, "action")).length;
  const cashValue = live
    .filter((collab) => collab.compensationType !== "barter")
    .reduce((total, collab) => total + collab.amountTotal, 0);
  const sideLabel = WORKSPACE_SIDE_LABELS[side].toLowerCase();
  const hasScoped = scoped.length > 0;
  const hasLive = live.length > 0;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-4">
      <h1 className="sr-only">Collabs</h1>

      {/* Inbox panel — nearly full viewport tall; page + footer scroll below. */}
      <div className="flex min-h-[calc(100dvh-5.5rem)] overflow-hidden rounded-xl border border-line bg-surface">
        {/* Every collab, not just the live ones: the rail opens on Active and its
            status filter reaches the rest without leaving the page. */}
        <div className="w-full shrink-0 lg:w-[17rem] lg:border-r lg:border-line">
          <CollabRail collabs={collabs} />
        </div>

        {/* Below lg the rail is the whole screen, so there is nothing to hint at. */}
        <div className="hidden min-w-0 flex-1 items-center justify-center p-10 lg:flex">
          <div className="max-w-xs text-center">
            <span className="inline-flex text-ink-subtle/70" aria-hidden="true">
              <ThreadIcon />
            </span>
            {hasScoped && hasLive ? (
              <>
                <p className="mt-3 text-sm font-medium text-ink">Pick a collab to open the thread</p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-subtle">
                  {live.length} active
                  {canToggle ? ` ${sideLabel}` : ""}
                  {cashValue > 0 ? ` · ${formatINR(cashValue)}` : ""}
                  {waiting > 0 ? ` · ${waiting} waiting on you` : ""}. Switch the status filter for
                  finished work, or see it all in{" "}
                  <Link href="/work-history" className="font-medium text-brand-700 hover:underline">
                    History
                  </Link>
                  .
                </p>
              </>
            ) : hasScoped ? (
              <>
                <p className="mt-3 text-sm font-medium text-ink">Pick a collab to open the thread</p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-subtle">
                  Basic offers live under Other until accepted. Switch the status filter, or see
                  finished work in{" "}
                  <Link href="/work-history" className="font-medium text-brand-700 hover:underline">
                    History
                  </Link>
                  .
                </p>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm font-medium text-ink">
                  No {sideLabel} collabs
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-subtle">
                  Switch to{" "}
                  {side === "creator"
                    ? WORKSPACE_SIDE_LABELS.brand
                    : WORKSPACE_SIDE_LABELS.creator}{" "}
                  to see the other side, or browse everything in{" "}
                  <Link href="/work-history" className="font-medium text-brand-700 hover:underline">
                    History
                  </Link>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NoCollabsBody({
  viewer,
}: {
  viewer: NonNullable<ReturnType<typeof useViewer>["viewer"]>;
}) {
  if (viewer.creator?.profilePath) {
    return (
      <>
        Share{" "}
        <Link href={viewer.creator.profilePath} className="font-medium text-brand-700">
          your page link
        </Link>{" "}
        in your bio for inbound offers.
      </>
    );
  }

  return viewer.creator
    ? "Claim a username on your overview to get your public page link."
    : "Offers you send from a creator's page show up here.";
}

function ThreadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.5} className="size-8 stroke-current">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM21 12c0 4.556-4.03 8.25-9 8.25a9.76 9.76 0 0 1-2.555-.337A5.97 5.97 0 0 1 5.41 20.97a5.97 5.97 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
      />
    </svg>
  );
}
